import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { delIcon, editIcon, fileIcon as FileIcon, linkIcon as LinkIcon } from "../../assets/icons";
import Button from "../../components/Buttons/Button";
import IconButton from "../../components/Buttons/IconButton";
import { ConfirmModal, CreateModal } from "../../components/Modals";
import Sidebar from "../../components/Sidebar";
import { useNotify } from "../../context/NotificationContext";
import { fetchCategories } from "../../services/courseService/courseService";
import { deleteLesson } from "../../services/lessonService/lessonService";
import {
  deleteKnowledgeBaseCourse,
  fetchKnowledgeBaseCourse,
  fetchKnowledgeBaseLessons,
  updateKnowledgeBaseCourse,
} from "../../services/knowledgeBaseService/knowledgeBaseService";
import type {
  KnowledgeBaseCourse,
  KnowledgeBaseLesson,
} from "../../services/knowledgeBaseService/knowledgeBaseService.types";
import { logger } from "../../utils/logger";
import { CoursesPageContainer } from "../CoursesPage/CoursesPage.styles";
import StudentBreadcrumbs from "../StudentPortal/StudentBreadcrumbs";
import KnowledgeBaseLessonEditModal from "./KnowledgeBaseLessonEditModal";
import {
  CourseLessonPageShell,
  DemoEmptyState,
  EntityDescription,
  EntityMeta,
  EntityTitle,
  LessonChevron,
  LessonContentSection,
  LessonMain,
  LessonMark,
  LessonMarkIcon,
  LessonMarks,
  LessonOrder,
  LessonsList,
  LessonTitle,
  SectionHeading,
} from "../StudentPortal/StudentCourseLesson.styles";
import { EmptyState, LoadingState } from "../StudentPortal/studentPortal.styles";
import {
  KnowledgeBaseCourseHeader,
  KnowledgeBaseCourseEditAction,
  KnowledgeBaseCourseHeaderMain,
  KnowledgeBaseLessonRowEdit,
  KnowledgeBaseLessonRowLink,
  KnowledgeBaseLessonRowShell,
  KnowledgeBaseStudentMain,
} from "./KnowledgeBasePage.styles";

const KnowledgeBaseCoursePage: React.FC = () => {
  const { courseId } = useParams();
  const courseIdNumber = Number(courseId);
  const [course, setCourse] = useState<KnowledgeBaseCourse | null>(null);
  const [lessons, setLessons] = useState<KnowledgeBaseLesson[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingLessonId, setEditingLessonId] = useState<number | null>(null);
  const [courseDeleteOpen, setCourseDeleteOpen] = useState(false);
  const [deletingLesson, setDeletingLesson] = useState<KnowledgeBaseLesson | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const toast = useNotify();
  const navigate = useNavigate();

  useEffect(() => {
    if (!courseIdNumber) return;
    let active = true;
    setLoading(true);
    setError("");

    Promise.all([
      fetchKnowledgeBaseCourse(courseIdNumber),
      fetchKnowledgeBaseLessons(courseIdNumber),
    ])
      .then(([courseData, lessonItems]) => {
        if (!active) return;
        setCourse(courseData);
        setLessons(lessonItems);
      })
      .catch((requestError) => {
        logger.error("Ошибка загрузки курса Базы знаний:", requestError);
        if (!active) return;
        setError("Курс недоступен. Для просмотра нужен тариф LISRENT PRO или LISRENT EXPERT.");
        toast.show({
          severity: "error",
          summary: "Ошибка",
          detail: "Не удалось открыть курс Базы знаний",
          life: 3000,
        });
      })
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
    };
  }, [courseIdNumber, toast]);

  useEffect(() => {
    fetchCategories()
      .then(setCategories)
      .catch((requestError) => logger.error("Ошибка загрузки категорий Базы знаний:", requestError));
  }, []);

  const handleDeleteCourse = async () => {
    if (!course) return;

    try {
      await deleteKnowledgeBaseCourse(course.id);
      setCourseDeleteOpen(false);
      window.dispatchEvent(new Event("knowledge-base-status-changed"));
      toast.show({
        severity: "success",
        summary: "Удалено",
        detail: "Курс Базы знаний удалён",
        life: 2500,
      });
      navigate("/knowledge-base");
    } catch (requestError) {
      logger.error("Ошибка удаления курса Базы знаний:", requestError);
      toast.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Не удалось удалить курс Базы знаний",
        life: 3000,
      });
    }
  };

  const handleDeleteLesson = async () => {
    if (!course || !deletingLesson) return;

    try {
      await deleteLesson(course.id, deletingLesson.id);
      setDeletingLesson(null);
      const lessonItems = await fetchKnowledgeBaseLessons(course.id);
      setLessons(lessonItems);
      toast.show({
        severity: "success",
        summary: "Удалено",
        detail: "Урок удалён",
        life: 2500,
      });
    } catch (requestError) {
      logger.error("Ошибка удаления урока Базы знаний:", requestError);
      toast.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Не удалось удалить урок",
        life: 3000,
      });
    }
  };

  const handleSaveCourse = async (formData: Record<string, string | File[] | null>) => {
    if (!course) return;

    setSaving(true);
    try {
      const updatedCourse = await updateKnowledgeBaseCourse(course.id, {
        title: String(formData.title || "").trim(),
        category: String(formData.category || "").trim(),
        description: String(formData.description || "").trim(),
      });
      setCourse(updatedCourse);
      setEditModalOpen(false);
      toast.show({
        severity: "success",
        summary: "Готово",
        detail: "Курс обновлён",
        life: 2500,
      });
    } catch (requestError) {
      logger.error("Ошибка обновления курса Базы знаний:", requestError);
      toast.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Не удалось обновить курс",
        life: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <CoursesPageContainer>
      <Sidebar />
      <KnowledgeBaseStudentMain>
        {loading ? (
          <LoadingState>Загружаем курс…</LoadingState>
        ) : error || !course ? (
          <EmptyState>{error || "Курс не найден."}</EmptyState>
        ) : (
          <CourseLessonPageShell>
            <StudentBreadcrumbs
              items={[
                { label: "База знаний", to: "/knowledge-base" },
                { label: course.title },
              ]}
            />

            <KnowledgeBaseCourseHeader>
              <KnowledgeBaseCourseHeaderMain>
                <EntityTitle>{course.title}</EntityTitle>
                <EntityMeta>
                  {course.instructor.organization_name} · {course.category}
                </EntityMeta>
              </KnowledgeBaseCourseHeaderMain>

              {course.can_manage ? (
                <KnowledgeBaseCourseEditAction>
                  <Button
                    type="button"
                    variant="primary"
                    size="md"
                    onClick={() => setEditModalOpen(true)}
                  >
                    Редактировать курс
                  </Button>
                  <Button
                    type="button"
                    variant="dangerSecondary"
                    size="md"
                    onClick={() => setCourseDeleteOpen(true)}
                  >
                    Удалить курс
                  </Button>
                </KnowledgeBaseCourseEditAction>
              ) : null}
            </KnowledgeBaseCourseHeader>

            {course.description ? (
              <>
                <SectionHeading>Описание курса</SectionHeading>
                <LessonContentSection>
                  <EntityDescription>{course.description}</EntityDescription>
                </LessonContentSection>
              </>
            ) : null}

            <SectionHeading>Уроки</SectionHeading>
            {lessons.length === 0 ? (
              <DemoEmptyState>В курсе пока нет уроков.</DemoEmptyState>
            ) : (
              <LessonsList>
                {lessons.map((lesson) => {
                  const hasVideo =
                    lesson.has_video ||
                    lesson.files_from_storage.some((item) => item.type === "video");
                  const hasMaterials = lesson.files_from_storage.some(
                    (item) => item.type === "document",
                  );
                  const hasMarks = Boolean(
                    lesson.is_new ||
                      lesson.category_title ||
                      hasVideo ||
                      hasMaterials ||
                      lesson.with_link ||
                      lesson.with_assignments,
                  );

                  return (
                    <KnowledgeBaseLessonRowShell key={lesson.id}>
                      <KnowledgeBaseLessonRowLink
                        to={`/knowledge-base/${course.id}/lessons/${lesson.id}`}
                        state={{
                          courseTitle: course.title,
                          lessonCount: lessons.length,
                        }}
                        $locked={Boolean(lesson.is_locked)}
                        aria-disabled={lesson.is_locked ? true : undefined}
                        title={lesson.is_locked ? lesson.lock_reason : undefined}
                        onClick={(event) => {
                          if (lesson.is_locked) event.preventDefault();
                        }}
                      >
                        <LessonOrder>{lesson.order}</LessonOrder>
                        <LessonMain>
                          <LessonTitle>
                            {lesson.is_locked ? `🔒 ${lesson.title}` : lesson.title}
                          </LessonTitle>
                          {lesson.is_locked && lesson.lock_reason ? (
                            <LessonMarks>
                              <span>{lesson.lock_reason}</span>
                            </LessonMarks>
                          ) : hasMarks ? (
                            <LessonMarks>
                              {lesson.is_new ? <LessonMark>Новое</LessonMark> : null}
                              {lesson.category_title ? (
                                <span>{lesson.category_title}</span>
                              ) : null}
                              {hasVideo ? (
                                <LessonMark>
                                  <LessonMarkIcon>
                                    <FileIcon aria-hidden="true" />
                                  </LessonMarkIcon>
                                  видео
                                </LessonMark>
                              ) : null}
                              {hasMaterials ? (
                                <LessonMark>
                                  <LessonMarkIcon>
                                    <FileIcon aria-hidden="true" />
                                  </LessonMarkIcon>
                                  материалы
                                </LessonMark>
                              ) : null}
                              {lesson.with_link ? (
                                <LessonMark>
                                  <LessonMarkIcon>
                                    <LinkIcon aria-hidden="true" />
                                  </LessonMarkIcon>
                                  ссылка
                                </LessonMark>
                              ) : null}
                              {lesson.with_assignments ? <span>домашнее задание</span> : null}
                            </LessonMarks>
                          ) : null}
                        </LessonMain>
                        {!course.can_manage ? <LessonChevron aria-hidden="true" /> : null}
                      </KnowledgeBaseLessonRowLink>

                      {course.can_manage ? (
                        <KnowledgeBaseLessonRowEdit>
                          <IconButton
                            icon={editIcon}
                            altText="Редактировать урок"
                            onClick={() => setEditingLessonId(lesson.id)}
                          />
                          <IconButton
                            icon={delIcon}
                            tone="danger"
                            altText="Удалить урок"
                            onClick={() => setDeletingLesson(lesson)}
                          />
                        </KnowledgeBaseLessonRowEdit>
                      ) : null}
                    </KnowledgeBaseLessonRowShell>
                  );
                })}
              </LessonsList>
            )}

            <KnowledgeBaseLessonEditModal
              open={editingLessonId !== null}
              courseId={course.id}
              lessonId={editingLessonId}
              onClose={() => setEditingLessonId(null)}
              onSaved={async () => {
                const lessonItems = await fetchKnowledgeBaseLessons(course.id);
                setLessons(lessonItems);
              }}
            />

            <ConfirmModal
              isOpen={courseDeleteOpen}
              title="Удаление курса"
              message={"Вы уверены, что хотите удалить курс «" + course.title + "»?"}
              confirmButtonText="Удалить"
              confirmVariant="danger"
              onConfirm={() => void handleDeleteCourse()}
              onCancel={() => setCourseDeleteOpen(false)}
            />

            <ConfirmModal
              isOpen={Boolean(deletingLesson)}
              title="Удаление урока"
              message={"Вы уверены, что хотите удалить урок «" + (deletingLesson?.title || "") + "»?"}
              confirmButtonText="Удалить"
              confirmVariant="danger"
              onConfirm={() => void handleDeleteLesson()}
              onCancel={() => setDeletingLesson(null)}
            />

            {editModalOpen ? (
              <CreateModal
                title="Редактировать курс"
                fields={[
                  { name: "title", type: "text", placeholder: "Название", required: true },
                  {
                    name: "category",
                    type: "selectWithInput",
                    placeholder: "Категория",
                    options: categories,
                    required: true,
                  },
                  { name: "description", type: "textarea", placeholder: "Описание" },
                ]}
                onSubmit={handleSaveCourse}
                submitButtonText="Сохранить"
                onCancel={() => setEditModalOpen(false)}
                hasDragAndDrop={false}
                initialValues={{
                  title: course.title,
                  category: course.category,
                  description: course.description,
                }}
                isLoading={saving}
              />
            ) : null}
          </CourseLessonPageShell>
        )}
      </KnowledgeBaseStudentMain>
    </CoursesPageContainer>
  );
};

export default KnowledgeBaseCoursePage;
